import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, MapPin, Building2, Phone, AlertCircle, Loader2 } from 'lucide-react';
import { useBranch } from '@/contexts/BranchContext';
import { useAuth } from '@/contexts/AuthContext';
import { Branch } from '@/types';
import { getFranchiseIdFromUrl } from '@/config/franchiseUrls';
import { useMultipleBranchQueueCounts } from '@/hooks/useQueueCount';

interface BranchSelectionProps {
  onSelectBranch: (branch: Branch) => void;
  /** Notifies the parent once active branches are loaded (used for auto-selection / restore) */
  onBranchesLoaded?: (branches: Branch[]) => void;
}

export function BranchSelection({ onSelectBranch, onBranchesLoaded }: BranchSelectionProps) {
  const { t } = useTranslation();
  const { user, customClaims } = useAuth();
  const { branches, loading, listBranches } = useBranch();

  // CRITICAL: Get franchiseId from multiple sources with priority
  // 1. User profile franchiseId (highest priority for registered users)
  // 2. Custom claims franchiseId
  // 3. URL hostname mapping (for new users accessing via franchise URL)
  const franchiseId = user?.franchiseId || customClaims?.franchiseId || getFranchiseIdFromUrl();

  // Calculate active branches BEFORE any hooks that depend on them
  const activeBranches = useMemo(
    () => branches?.filter((b) => b.isActive) || [],
    [branches]
  );

  // Get branch IDs for queue count hook - MUST be called before any early returns
  const branchIds = useMemo(
    () => activeBranches.map((b) => b.branchId),
    [activeBranches]
  );

  // Get real-time queue counts for all branches - MUST be called before any early returns
  const queueCounts = useMultipleBranchQueueCounts(branchIds);

  useEffect(() => {
    // CRITICAL: Must have franchiseId to prevent showing branches from ALL franchises
    if (!franchiseId) {
      console.error('❌ No franchiseId available - cannot load branches');
      console.error('❌ User may see branches from ALL franchises!');
      console.error('❌ Check that user has franchiseId in their profile');
      return;
    }

    // Load branches ONLY for the user's franchise
    listBranches({ franchiseId, isActive: true });
  }, [franchiseId]);

  // Notify parent when active branches are available (auto-selection / restore)
  useEffect(() => {
    if (!loading && activeBranches.length > 0 && onBranchesLoaded) {
      onBranchesLoaded(activeBranches);
    }
  }, [loading, activeBranches, onBranchesLoaded]);

  // Early returns AFTER all hooks are called
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-ink-subtle animate-spin" aria-hidden="true" />
      </div>
    );
  }

  // Show error if no franchiseId available
  if (!franchiseId) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-surface border border-danger/40 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-danger shrink-0" aria-hidden="true" />
            <h3 className="font-sans text-lg font-semibold text-ink">
              {t('booking.branchSelection.configError')}
            </h3>
          </div>
          <p className="text-sm text-ink-muted mb-2">
            {t('booking.branchSelection.franchiseError')}
          </p>
          <p className="text-sm text-ink-subtle">
            {t('booking.branchSelection.contactSupport')}
          </p>
        </div>
      </div>
    );
  }

  if (activeBranches.length === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-8 h-8 text-ink-subtle mx-auto mb-3" aria-hidden="true" />
        <p className="text-ink-muted">
          {t('booking.branchSelection.noBranches')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-sans text-xl font-semibold tracking-tight text-ink mb-1 flex items-center justify-center gap-2">
        <MapPin className="w-5 h-5 shrink-0 text-ink-subtle" aria-hidden="true" />
        {t('booking.branchSelection.title')}
      </h2>
      <p className="text-sm text-ink-muted text-center mb-6">
        {t('booking.branchSelection.subtitle')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeBranches.map((branch) => (
          <button
            key={branch.branchId}
            onClick={() => onSelectBranch(branch)}
            className="group bg-surface rounded-xl p-5 border border-hairline hover:border-accent/60 transition-colors text-left"
          >
            {/* Branch Image */}
            <div className="mb-4 h-40 rounded-lg overflow-hidden bg-surface-3 flex items-center justify-center">
              {branch.photo ? (
                <img
                  src={branch.photo}
                  alt={branch.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <MapPin className="w-8 h-8 text-ink-subtle" aria-hidden="true" />
              )}
            </div>

            {/* Branch Info */}
            <h3 className="font-sans text-lg font-semibold text-ink mb-2">
              {branch.name}
            </h3>
            <div className="space-y-1.5 text-sm text-ink-muted mb-4">
              <p className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-ink-subtle shrink-0 mt-0.5" aria-hidden="true" />
                <span>{branch.address}</span>
              </p>
              <p className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
                <span>{branch.city}, {branch.province}</span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
                <span>{branch.phone}</span>
              </p>
            </div>

            {/* Queue Count & Open/Closed Status */}
            <div className="pt-4 border-t border-hairline space-y-3">
              {/* Queue Count */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-muted flex items-center gap-2">
                  <Users className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
                  {t('booking.branchSelection.queue', 'En cola')}
                </span>
                <span className="text-sm font-semibold text-ink tabular-nums flex items-center gap-1">
                  {queueCounts[branch.branchId]?.loading ? (
                    <span className="text-ink-subtle">…</span>
                  ) : (
                    <>
                      {queueCounts[branch.branchId]?.count || 0}
                      <span className="hidden sm:inline font-normal text-ink-muted">
                        {' '}{(queueCounts[branch.branchId]?.count || 0) === 1
                          ? t('booking.branchSelection.person', 'persona')
                          : t('booking.branchSelection.people', 'personas')}
                      </span>
                    </>
                  )}
                </span>
              </div>

              {/* Open/Closed Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-muted">
                  {t('booking.branchSelection.status')}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                  isOpenNow(branch) ? 'text-success' : 'text-ink-subtle'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isOpenNow(branch) ? 'bg-success' : 'bg-ink-subtle'}`} aria-hidden="true" />
                  {isOpenNow(branch) ? t('booking.branchSelection.open') : t('booking.branchSelection.closed')}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Helper function to check if branch is open now
function isOpenNow(branch: Branch): boolean {
  const now = new Date();
  const day = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof typeof branch.schedule;
  const daySchedule = branch.schedule[day];

  if (!daySchedule.isOpen) return false;

  const currentTime = now.toTimeString().slice(0, 5);
  return currentTime >= (daySchedule.open || '') && currentTime <= (daySchedule.close || '');
}
